import React, { Fragment } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { EmailSettings } from '../../../../core/api/models';
import ErrorMessage from '../../../../components/text/ErrorMessage';

const UpdateForm: React.FunctionComponent<{
  onSubmit: (data: EmailSettings) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  data?: EmailSettings;
  error?: string;
}> = (props) => {
  const { control, handleSubmit, errors, watch } = useForm<EmailSettings>({
    reValidateMode: 'onChange',
    mode: 'onSubmit',
    shouldUnregister: true,
    defaultValues: props.data,
  });

  const enabled = watch('sendMail');

  const renderEnabled = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          {/* <label className="font-sm text-uppercase">Franchisee</label> */}
          <Controller
            control={control}
            name="sendMail"
            defaultValue={props.data?.sendMail}
            render={({ onChange, value, name }) => (
              <Fragment>
                <input
                  type="checkbox"
                  id={name}
                  checked={value}
                  onChange={(e) => onChange(e.target.checked)}
                />
                <label htmlFor={name}>Email Enabled</label>
              </Fragment>
            )}
          />
          <ErrorMessage>{errors.sendMail?.message}</ErrorMessage>
        </div>
      </div>
    );
  };
  const renderSMTPHost = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">SMTP Host</label>
          <Controller
            control={control}
            rules={{
              validate: {
                required: (value) =>
                  !enabled || !!value || 'SMTP Host is required',
              },
            }}
            name="host"
            defaultValue={props.data?.host}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="SMTP Host"
                name={name}
                value={value}
                onChange={onChange}
                id="host"
              />
            )}
          />
          <ErrorMessage>{errors.host?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderSMTPPort = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">SMTP Port</label>
          <Controller
            control={control}
            rules={{
              validate: {
                required: (value) =>
                  !enabled || !!value || 'SMTP Port is required',
                numberOnly: (value) =>
                  !enabled ||
                  new RegExp('\\d+').test(value) ||
                  'Invalid port number',
              },
            }}
            name="port"
            defaultValue={props.data?.port}
            render={({ onChange, value, name }) => (
              <input
                type="number"
                min={0}
                className="form-control rounded-sm font-weight-semibold"
                placeholder="SMTP Port"
                name={name}
                value={value}
                onChange={onChange}
                id="port"
              />
            )}
          />
          <ErrorMessage>{errors.port?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderSMTPUsername = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">SMTP Username</label>
          <Controller
            control={control}
            rules={{
              validate: {
                required: (value) =>
                  !enabled || !!value || 'SMTP Username is required',
              },
            }}
            name="username"
            defaultValue={props.data?.username}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="SMTP Username"
                name={name}
                value={value}
                onChange={onChange}
                id="username"
              />
            )}
          />
          <ErrorMessage>{errors.username?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderSMTPPassword = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">SMTP Password</label>
          <Controller
            control={control}
            rules={{
              validate: {
                required: (value) =>
                  !enabled || !!value || 'SMTP Password is required',
              },
            }}
            name="password"
            defaultValue={props.data?.password}
            render={({ onChange, value, name }) => (
              <input
                type="password"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="SMTP Password"
                name={name}
                value={value}
                defaultValue={value}
                onChange={onChange}
                id="password"
              />
            )}
          />
          <ErrorMessage>{errors.password?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderSMTPFromAddress = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">SMTP From Address</label>
          <Controller
            control={control}
            rules={{
              validate: {
                required: (value) =>
                  !enabled || !!value || 'SMTP From Address is required',
              },
            }}
            name="from"
            defaultValue={props.data?.from}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="SMTP From Address"
                name={name}
                value={value}
                onChange={onChange}
                id="from"
              />
            )}
          />
          <ErrorMessage>{errors.from?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderSMTPSSL = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <Controller
            control={control}
            name="ssl"
            defaultValue={props.data?.ssl}
            render={({ onChange, value, name }) => (
              <Fragment>
                <input
                  type="checkbox"
                  id={name}
                  checked={value}
                  onChange={(e) => onChange(e.target.checked)}
                />
                <label htmlFor={name}>SSL</label>
              </Fragment>
            )}
          />
        </div>
      </div>
    );
  };

  const renderSESConfigName = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">SES Configuration Name</label>
          <Controller
            control={control}
            // rules={{
            //   validate: {
            //     required: (value) =>
            //       !enabled || !!value || 'SES Configuration Name is required',
            //   },
            // }}
            name="sesConfigName"
            defaultValue={props.data?.sesConfigName}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="SES Configuration Name"
                name={name}
                value={value}
                onChange={onChange}
                id="sesConfigName"
              />
            )}
          />
          <ErrorMessage>{errors.sesConfigName?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderButton = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3 text-right pt-3">
          <button
            type="button"
            className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
            onClick={props.onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
            disabled={props.isSubmitting}
          >
            Update
          </button>
        </div>
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit(props.onSubmit)}>
      <div className="p-4">
        <h4 className="mb-3 font-weight-semibold">Update Email Settings</h4>
        <div className="row gutter-10">
          {renderEnabled()}
          {renderSMTPHost()}
          {renderSMTPPort()}
          {renderSMTPUsername()}
          {renderSMTPPassword()}
          {renderSMTPFromAddress()}
          {renderSMTPSSL()}
          {renderSESConfigName()}
          <ErrorMessage>{props.error}</ErrorMessage>
          {renderButton()}
        </div>
      </div>
    </form>
  );
};

export default UpdateForm;
